import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Save, Clock, Volume2, VolumeX, Coffee, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import { useSettings } from '../../contexts/SettingsContext';

const BreakEditor = ({ breakItem }) => {
  const { t } = useTranslation('breaks');
  const daysOfWeek = [
    { key: 'monday', label: t('daysFull.monday') },
    { key: 'tuesday', label: t('daysFull.tuesday') },
    { key: 'wednesday', label: t('daysFull.wednesday') },
    { key: 'thursday', label: t('daysFull.thursday') },
    { key: 'friday', label: t('daysFull.friday') },
    { key: 'saturday', label: t('daysFull.saturday') },
    { key: 'sunday', label: t('daysFull.sunday') },
  ];
  const { updateBreak, selectBreak } = useBreakReminder();
  const { preferences } = useSettings();
  const [name, setName] = useState(breakItem?.name || '');
  const [startTime, setStartTime] = useState(breakItem?.startTime || '10:00');
  const [startTimeError, setStartTimeError] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(breakItem?.durationMinutes || 15);
  const [activeDays, setActiveDays] = useState(breakItem?.activeDays || {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  });
  const globalSoundEnabled = preferences.playNotificationSound !== false;
  const [soundEnabled, setSoundEnabled] = useState(
    breakItem?.soundEnabled !== undefined ? breakItem.soundEnabled && globalSoundEnabled : globalSoundEnabled,
  );
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const saveNotificationTimerRef = useRef(null);

  // Cleanup save notification timer on unmount
  useEffect(() => {
    return () => {
      if (saveNotificationTimerRef.current) {
        clearTimeout(saveNotificationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (breakItem) {
      setName(breakItem.name || '');
      setStartTime(breakItem.startTime || '10:00');
      setStartTimeError('');
      setDurationMinutes(breakItem.durationMinutes || 15);
      setActiveDays(
        breakItem.activeDays || {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
      );
      setSoundEnabled(
        breakItem.soundEnabled !== undefined
          ? breakItem.soundEnabled && globalSoundEnabled
          : globalSoundEnabled,
      );
    }
  }, [breakItem, globalSoundEnabled]);

  useEffect(() => {
    if (!globalSoundEnabled) {
      setSoundEnabled(false);
    }
  }, [globalSoundEnabled]);

  const handleSave = () => {
    if (breakItem) {
      const normalizedTime = normalizeTimeString(startTime);
      if (!normalizedTime) {
        setStartTimeError(t('editor.timeError'));
        return;
      }
      updateBreak({
        ...breakItem,
        name: name || t('editor.defaultBreakName'),
        startTime: normalizedTime,
        durationMinutes,
        activeDays,
        soundEnabled,
      });
      
      // Bildirim göster
      setShowSaveNotification(true);
      
      // Bildirimi 3 saniye sonra gizle
      if (saveNotificationTimerRef.current) {
        clearTimeout(saveNotificationTimerRef.current);
      }
      saveNotificationTimerRef.current = setTimeout(() => {
        setShowSaveNotification(false);
      }, 3000);
    }
  };

  const toggleDay = (dayKey) => {
    setActiveDays((prev) => ({
      ...prev,
      [dayKey]: !prev[dayKey],
    }));
  };

  const selectAllWeekdays = () => {
    setActiveDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    });
  };

  const selectAllDays = () => {
    const allTrue = Object.values(activeDays).every((day) => day === true);
    const newState = {
      monday: !allTrue,
      tuesday: !allTrue,
      wednesday: !allTrue,
      thursday: !allTrue,
      friday: !allTrue,
      saturday: !allTrue,
      sunday: !allTrue,
    };
    setActiveDays(newState);
  };

  if (!breakItem) return null;

  const normalizeTimeString = (value) => {
    if (!value) return null;
    const trimmed = value.trim();
    const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    return null;
  };

  const handleTimeChange = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      setStartTime('');
      setStartTimeError('');
      return;
    }

    if (digits.length > 4) {
      return;
    }

    const hourPart = digits.slice(0, 2);
    const minutePart = digits.slice(2);

    if (hourPart.length === 1 && parseInt(hourPart, 10) > 2) {
      return;
    }

    if (hourPart.length === 2 && parseInt(hourPart, 10) > 23) {
      return;
    }

    if (minutePart.length > 0) {
      if (minutePart.length > 2) {
        return;
      }
      if (minutePart.length === 2 && parseInt(minutePart, 10) > 59) {
        return;
      }
    }

    let formatted = hourPart;
    if (hourPart.length === 2 && minutePart.length === 0) {
      formatted = `${hourPart}:`;
    }
    if (minutePart.length > 0) {
      formatted = `${hourPart}:${minutePart}`;
    }

    setStartTime(formatted);
    setStartTimeError('');
  };

  const inputBase =
    'w-full bg-transparent border border-slate-300 dark:border-white/10 dark:bg-[#161f31] rounded-md px-4 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#c8d8eb] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col bg-white dark:bg-[var(--color-bg-base)] border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/10 bg-white dark:bg-[var(--color-bg-base)]">
        <div className="flex items-center gap-4">
          <button
            onClick={() => selectBreak(null)}
            className="p-2 rounded-md border border-slate-200 dark:border-white/10 text-slate-600 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center">
              <Coffee className="w-5 h-5 text-slate-600 dark:text-[var(--color-text-secondary)]" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('editor.title')}</h2>
              <p className="text-xs text-slate-500 dark:text-[var(--color-text-secondary)]">{t('editor.subtitle')}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 relative">
          <AnimatePresence>
            {showSaveNotification && (
              <motion.div
                initial={{ opacity: 0, x: -20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -20, scale: 0.9 }}
                transition={{ duration: 0.3, type: 'spring' }}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 dark:border-white/10 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-sm font-medium"
              >
                <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                {t('editor.saved')}
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
          >
            <Save className="w-4 h-4" strokeWidth={2.5} />
            {t('common:save')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <label htmlFor="breakName" className="block text-sm font-medium text-slate-700 dark:text-[var(--color-text-secondary)] mb-2">
              {t('editor.nameLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="breakName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputBase}
              placeholder={t('editor.namePlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-[var(--color-text-secondary)] mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                {t('editor.startTimeLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={startTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                onBlur={() => {
                  const normalized = normalizeTimeString(startTime);
                  if (normalized) {
                    setStartTime(normalized);
                    setStartTimeError('');
                  } else {
                    setStartTimeError(t('editor.timeError'));
                  }
                }}
                placeholder="HH:MM"
                required
                maxLength={5}
                className={`${inputBase} ${startTimeError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                inputMode="numeric"
              />
              {startTimeError && <p className="text-red-500 text-xs mt-1">{startTimeError}</p>}
            </div>
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-slate-700 dark:text-[var(--color-text-secondary)] mb-2">
                {t('editor.durationLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="duration"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                max="480"
                className={inputBase}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-[var(--color-text-secondary)]">{t('editor.activeDaysLabel')}</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllWeekdays}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-white/10 text-slate-600 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  {t('editor.weekdaysButton')}
                </button>
                <button
                  type="button"
                  onClick={selectAllDays}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-white/10 text-slate-600 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  {t('editor.allButton')}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {daysOfWeek.map((day) => (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => toggleDay(day.key)}
                  className={`p-3 rounded-md border text-sm font-medium transition-colors ${
                    activeDays[day.key]
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400'
                      : 'bg-white dark:bg-[var(--color-bg-elevated)] border-slate-200 dark:border-white/10 text-slate-600 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)]'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white dark:bg-[var(--color-bg-elevated)] border border-slate-200 dark:border-white/10">
            <div className="flex items-center gap-3">
              {globalSoundEnabled && soundEnabled ? (
                <Volume2 className="w-5 h-5 text-slate-600 dark:text-[var(--color-text-secondary)]" strokeWidth={2} />
              ) : (
                <VolumeX className="w-5 h-5 text-slate-400 dark:text-[var(--color-text-secondary)]" strokeWidth={2} />
              )}
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{t('editor.notificationSoundLabel')}</p>
                <p className="text-xs text-slate-500 dark:text-[var(--color-text-secondary)]">{t('editor.notificationSoundDescription')}</p>
                {!globalSoundEnabled && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('editor.enableSoundFirst')}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => globalSoundEnabled && setSoundEnabled(!soundEnabled)}
              disabled={!globalSoundEnabled}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                globalSoundEnabled && soundEnabled ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'
              } ${!globalSoundEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  globalSoundEnabled && soundEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BreakEditor;
